import _ from 'lodash';
import React from 'react';
import cloneDeep from 'lodash/cloneDeep';

/**
 * nodeId - Generates a unique key for list items. Keys help React identify
 *          which items have changed, are added, or are removed. Incase the
 *          dataset does not have a unique "id" attribute we can use this function to generate one.
 *          read more (https://facebook.github.io/react/docs/lists-and-keys.html)
 *
 * @param {type} prefix Description
 *
 * @return {type} Description
 */
export function nodeId(prefix) {
  prefix = prefix ? `${prefix}_` : '';
  const identifier = (Math.random() + parseInt(_.uniqueId(), 10)).toString(16);
  return `${prefix}${identifier}`;
}


/**
 * getErrorField - Extract nested error messages from API
 * response error object
 *
 * @param  {[type]} field       [description]
 * @param  {[type]} parentField [description]
 * @param  {Array}  error       [description]
 * @return {[type]}             [description]
 */
export function getErrorField(field, parentField, error = []) {
  const  groupError = _.find(error, (o) => (o.name === parentField));
  return groupError && _.find(groupError.fields, (o) => (o.name === field));
}

/**
 * checkNullSubmit - check if format to be posted are all nulls
 * @param  {Array} post  Array of objects, one for each input, with the following keys {post, selected, label}
 * @return {Bool}        True for not all nulls in submission array, otherwise confirm dialog for true/false
 */
export function checkNullSubmit(post) {
  let nullPosts = post.filter((post) => !post.post && (!post.selected ? false : (post.selected.length < 1)));

  let nullPostsString = nullPosts.reduce((prev, elem, index, arr) => {
    if (prev.length < 1) {
      return `${elem.label}`;
    }

    if (index < (arr.length - 1)) {
      return `${prev}, ${elem.label}`;
    }

    if (index === (arr.length - 1)) {
      return `${prev}, and ${elem.label}`;
    }
  }, '');

  if (nullPosts.length) {
    return confirm(`You have not selected any ${nullPostsString}. All options will be selected by default.`);
  } else {
    return true;
  }
}

export function hasClass(el, className) {
  if (el.classList) {
    return el.classList.contains(className);
  } else {
    return !!el.className.match(new RegExp(`(\\s|^)${className}(\\s|$)`));
  }
}

export function addClass(el, className) {
  if (el.classList) {
    el.classList.add(className);
  } else if (!hasClass(el, className)) {
    el.className = `${el.className} ${className}`;
  }
}

export function removeClass(el, className) {
  if (el.classList) {
    el.classList.remove(className);
  } else if (hasClass(el, className)) {
    const reg = new RegExp(`(\\s|^)${className}(\\s|$)`);
    el.className = el.className.replace(reg, ' ');
  }
}

export function addError(errors = [], newError, formName, fieldName) {
  const newErrorObj = {name: formName, fields: [{name: fieldName, messages: [{text: newError}]}]};

  const formError = errors.find((form) => form.name === formName);
  if (!formError) {
    errors = [...errors, newErrorObj];
    return errors;
  }

  const fieldError = formError.fields.find((fl) => fl.name === fieldName);
  if (!fieldError) {
    formError.fields = [...formError.fields, ...newErrorObj.fields];
  } else {
    fieldError.messages = [...fieldError.messages, {text: newError}];
  }

  return errors;
}

export function removeError(errors = [], formName = false, fieldName = false) {
  if (!fieldName) {
    return errors.filter((el) => el.name !== formName);
  }

  const formError = errors.find((form) => form.name === formName);
  if (formError) {
    formError.fields = formError.fields.filter((fl) => fl.name !== fieldName);
  }

  return errors;
}

/**
 * Remove a specific error by formName, fieldName and message
 *
 * @param array errors An error array as returned by our API
 * @param string formName The form whose errors we're interested in
 * @param string fieldName The field whose errors we're interested in
 * @param string targetMessage The message we want to remove
 * @return array A modified error array
 */
export function removeErrorByMessage(errors, formName, fieldName, targetMessage) {
  const formIndex = errors.findIndex((item) => {
    return item.name == formName;
  });
  if (formIndex == -1) {
    return errors;
  }
  const formErrors = errors[formIndex].fields;
  const fieldIndex = formErrors.findIndex((item) => {
    return item.name == fieldName;
  });
  if (fieldIndex == -1) {
    return errors;
  }

  const fieldErrors = formErrors[fieldIndex].messages;
  const messages = fieldErrors.filter((item) => {
    return item.text != targetMessage;
  });

  let newErrors = cloneDeep(errors);
  newErrors[formIndex].fields[fieldIndex].messages = messages;

  return newErrors;
}

/**
 * validateInputByOptions takes an array of inputs and compares them against
 *                        an array of options to verify if they are valid.
 *
 * @param  {Array}    inputValues input values.
 * @param  {Array}    options     all possible valid values.
 * @param  {function} eq          Compares elements in inputValues with
 *                                elements in options, returns true
 *                                if they are equal, false otherwise.

 * @param  {Array}    ignoreValues values to ignore in input if they exist.
 *
 * @return {Object}   {
 *                      invalid: Array(<invalid values>),
 *                      valid: Array(<valid values>),
 *                    }
 */
export function validateInputByOptions(inputValues, options, eq, ignoreValues = []) {
  let returnObject = {
    invalid: [],
    valid: [],
  };

  // Remove any input values that are present in ignoreValues.
  let ignoredValues = new Set();
  for (const val of inputValues) {
    for (const val2 of ignoreValues) {
      if (eq(val, val2)) {
        ignoredValues.add(val);
      }
    }
  }

  let checkInputValues = [];
  checkInputValues = inputValues.filter((el) => {
    return !ignoredValues.has(el);
  });

  // Return (aka do nothing) there are no valid input values.
  if (!checkInputValues.length) {
    return returnObject;
  }

  // Sort input values into valid and invalid.
  for (const inputValue of checkInputValues) {
    let foundValid = false;
    for (const option of options) {
      if (eq(inputValue, option)) {
        foundValid = true;
        // We are very specific in pushing the option object here
        // because the object option may contain extra properties not found
        // in  nputValue.
        returnObject.valid.push(option);
        break;
      }
    }

    if (!foundValid) {
      returnObject.invalid.push(inputValue);
    }
  }

  return returnObject;
}

//nodeIdFunc is a parameter so that portal's
//tests/pages/ProductCategoryPage/CreateProductCategoryForm.test.js
//can mock it properly
export function getValidationState(error, isWarning, nodeIdFunc = nodeId) {
  let errorList = null;
  let errorState = null;

  if (error) {
    errorState = isWarning ? 'warning' : 'error';

    errorList = (
      <ul>
        {error.messages.map((e) => <li key={nodeIdFunc()}>{e.text}</li>)}
      </ul>
    );
  }

  return [errorState, errorList];
}
